import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'
import { addToHistory } from '../utils/history'

interface DecodedJwt {
  header: Record<string, unknown> | null
  payload: Record<string, unknown> | null
  signature: string
  isValid: boolean
}

function JwtDecoder() {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [decoded, setDecoded] = useState<DecodedJwt | null>(null)
  const [error, setError] = useState('')

  const saveToHistory = useCallback((inputVal: string, outputVal: string) => {
    if (inputVal && outputVal) {
      addToHistory({
        tool: 'jwt-decoder',
        toolName: 'JWT Decoder',
        input: inputVal.substring(0, 500),
        output: outputVal.substring(0, 500),
      })
    }
  }, [])

  const decodeBase64Url = (str: string): string => {
    // Convert base64url to base64
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    // Add padding if needed
    const pad = base64.length % 4
    if (pad) {
      base64 += '='.repeat(4 - pad)
    }
    return decodeURIComponent(escape(atob(base64)))
  }

  const decodeJwt = (token: string) => {
    setInput(token)
    setError('')
    setDecoded(null)

    if (!token.trim()) {
      return
    }

    const parts = token.trim().split('.')
    
    if (parts.length !== 3) {
      setError(t('toolPages.jwtDecoder.invalidFormat'))
      return
    }

    try {
      const headerJson = decodeBase64Url(parts[0])
      const payloadJson = decodeBase64Url(parts[1])
      
      const header = JSON.parse(headerJson)
      const payload = JSON.parse(payloadJson)
      
      setDecoded({
        header,
        payload,
        signature: parts[2],
        isValid: true
      })
      
      saveToHistory(token, JSON.stringify({ header, payload }, null, 2))
    } catch (err) {
      setError(t('toolPages.jwtDecoder.decodeError'))
    }
  }

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    })
  }

  const getTimeStatus = (exp: number): { status: 'expired' | 'expiring' | 'valid'; message: string } => {
    const now = Date.now() / 1000
    const diff = exp - now
    
    if (diff < 0) {
      const elapsed = Math.abs(diff)
      if (elapsed < 60) return { status: 'expired', message: `${Math.floor(elapsed)} ${t('toolPages.jwtDecoder.seconds')} ${t('toolPages.jwtDecoder.ago')} ${t('toolPages.jwtDecoder.expired')}` }
      if (elapsed < 3600) return { status: 'expired', message: `${Math.floor(elapsed / 60)} ${t('toolPages.jwtDecoder.minutes')} ${t('toolPages.jwtDecoder.ago')} ${t('toolPages.jwtDecoder.expired')}` }
      if (elapsed < 86400) return { status: 'expired', message: `${Math.floor(elapsed / 3600)} ${t('toolPages.jwtDecoder.hours')} ${t('toolPages.jwtDecoder.ago')} ${t('toolPages.jwtDecoder.expired')}` }
      return { status: 'expired', message: `${Math.floor(elapsed / 86400)} ${t('toolPages.jwtDecoder.days')} ${t('toolPages.jwtDecoder.ago')} ${t('toolPages.jwtDecoder.expired')}` }
    }
    
    if (diff < 300) return { status: 'expiring', message: `${Math.floor(diff)} ${t('toolPages.jwtDecoder.seconds')} ${t('toolPages.jwtDecoder.in')}` }
    if (diff < 3600) return { status: 'expiring', message: `${Math.floor(diff / 60)} ${t('toolPages.jwtDecoder.minutes')} ${t('toolPages.jwtDecoder.in')}` }
    if (diff < 86400) return { status: 'valid', message: `${Math.floor(diff / 3600)} ${t('toolPages.jwtDecoder.hours')} ${t('toolPages.jwtDecoder.in')}` }
    return { status: 'valid', message: `${Math.floor(diff / 86400)} ${t('toolPages.jwtDecoder.days')} ${t('toolPages.jwtDecoder.in')}` }
  }

  const loadSample = () => {
    // Sample JWT with typical claims
    const header = { alg: 'HS256', typ: 'JWT' }
    const payload = {
      sub: '1234567890',
      name: 'Hong Gildong',
      email: 'hong@example.com',
      iat: Math.floor(Date.now() / 1000) - 3600,
      exp: Math.floor(Date.now() / 1000) + 3600,
      iss: 'allinonedecoder.com',
      aud: 'api.allinonedecoder.com',
      roles: ['user', 'admin']
    }
    
    const encodeBase64Url = (obj: object): string => {
      const json = JSON.stringify(obj)
      const base64 = btoa(unescape(encodeURIComponent(json)))
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
    }
    
    const sampleToken = `${encodeBase64Url(header)}.${encodeBase64Url(payload)}.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c`
    decodeJwt(sampleToken)
  }

  const handleClear = () => {
    setInput('')
    setDecoded(null)
    setError('')
  }

  const renderValue = (key: string, value: unknown): React.ReactNode => {
    // Handle timestamp fields
    if ((key === 'exp' || key === 'iat' || key === 'nbf') && typeof value === 'number') {
      const timeStatus = key === 'exp' ? getTimeStatus(value) : null
      return (
        <div className="space-y-1">
          <span className="text-primary-600 dark:text-primary-400">{value}</span>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {formatTimestamp(value)}
          </div>
          {timeStatus && (
            <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
              timeStatus.status === 'expired' 
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : timeStatus.status === 'expiring'
                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }`}>
              {timeStatus.message}
            </span>
          )}
        </div>
      )
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      return (
        <span className="text-primary-600 dark:text-primary-400">
          [{value.map((v, i) => (
            <span key={i}>
              {typeof v === 'string' ? `"${v}"` : JSON.stringify(v)}
              {i < value.length - 1 ? ', ' : ''}
            </span>
          ))}]
        </span>
      )
    }
    
    // Handle objects
    if (typeof value === 'object' && value !== null) {
      return (
        <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      )
    }
    
    // Handle strings
    if (typeof value === 'string') {
      return <span className="text-green-600 dark:text-green-400">"{value}"</span>
    }
    
    // Handle other primitives
    return <span className="text-primary-600 dark:text-primary-400">{JSON.stringify(value)}</span>
  }

  // Auto-refresh for time status
  const [, setTick] = useState(0)
  useEffect(() => {
    if (decoded?.payload?.exp) {
      const interval = setInterval(() => setTick(t => t + 1), 1000)
      return () => clearInterval(interval)
    }
  }, [decoded?.payload?.exp])

  return (
    <Layout showBackButton title={t('toolPages.jwtDecoder.title')}>
      <div className="tool-container">
        <p className="tool-description">
          {t('toolPages.jwtDecoder.description')}
        </p>

      {/* Input Section */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="section-title">{t('toolPages.jwtDecoder.tokenInput')}</h2>
          <div className="flex gap-2">
            <button onClick={loadSample} className="btn-outline text-sm">
              {t('common.sample')}
            </button>
            <button onClick={handleClear} className="btn-secondary text-sm">
              {t('common.clear')}
            </button>
          </div>
        </div>
        <textarea
          value={input}
          onChange={(e) => decodeJwt(e.target.value)}
          placeholder={t('toolPages.jwtDecoder.inputPlaceholder')}
          className="textarea h-32 scrollbar-thin font-mono text-sm"
          spellCheck={false}
        />
        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="error-message flex items-start gap-2">
              <span>⚠️</span>
              <span>{error}</span>
            </p>
          </div>
        )}
      </div>

      {/* Decoded Sections */}
      {decoded && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Header */}
          <div className="card border-t-4 border-t-red-500">
            <div className="flex justify-between items-center mb-4">
              <h2 className="section-title text-red-600 dark:text-red-400">{t('toolPages.jwtDecoder.header')}</h2>
              <CopyButton text={JSON.stringify(decoded.header, null, 2)} />
            </div>
            <div className="space-y-3">
              {decoded.header && Object.entries(decoded.header).map(([key, value]) => (
                <div key={key} className="flex flex-col">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{key}</span>
                  {renderValue(key, value)}
                </div>
              ))}
            </div>
          </div>

          {/* Payload */}
          <div className="card border-t-4 border-t-purple-500">
            <div className="flex justify-between items-center mb-4">
              <h2 className="section-title text-purple-600 dark:text-purple-400">{t('toolPages.jwtDecoder.payload')}</h2>
              <CopyButton text={JSON.stringify(decoded.payload, null, 2)} />
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
              {decoded.payload && Object.entries(decoded.payload).map(([key, value]) => (
                <div key={key} className="flex flex-col">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {key}
                    {key === 'exp' && ` (${t('toolPages.jwtDecoder.expiresIn')})`}
                    {key === 'iat' && ` (${t('toolPages.jwtDecoder.issuedAt')})`}
                    {key === 'nbf' && ` (${t('toolPages.jwtDecoder.notBefore')})`}
                    {key === 'sub' && ` (${t('toolPages.jwtDecoder.subject')})`}
                    {key === 'iss' && ` (${t('toolPages.jwtDecoder.issuer')})`}
                    {key === 'aud' && ` (${t('toolPages.jwtDecoder.audience')})`}
                  </span>
                  {renderValue(key, value)}
                </div>
              ))}
            </div>
          </div>

          {/* Signature */}
          <div className="card border-t-4 border-t-blue-500">
            <div className="flex justify-between items-center mb-4">
              <h2 className="section-title text-blue-600 dark:text-blue-400">{t('toolPages.jwtDecoder.signature')}</h2>
              <CopyButton text={decoded.signature} />
            </div>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
              <p className="font-mono text-xs break-all text-gray-700 dark:text-gray-300">
                {decoded.signature}
              </p>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {t('toolPages.jwtDecoder.signatureWarning')}
            </p>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-8 card bg-gray-50 dark:bg-gray-800/50">
        <h3 className="font-semibold mb-2">💡 JWT란?</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          JWT(JSON Web Token)는 두 당사자 간에 정보를 안전하게 전송하기 위한 개방형 표준(RFC 7519)입니다.
          주로 인증 및 정보 교환에 사용됩니다.
        </p>
        <h4 className="font-medium text-sm mb-1">JWT 구조:</h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong className="text-red-600 dark:text-red-400">Header</strong>: 토큰 유형과 서명 알고리즘 정보</li>
          <li><strong className="text-purple-600 dark:text-purple-400">Payload</strong>: 클레임(claims) - 사용자 데이터 및 메타데이터</li>
          <li><strong className="text-blue-600 dark:text-blue-400">Signature</strong>: 토큰의 무결성을 검증하기 위한 서명</li>
        </ul>
      </div>
      </div>
    </Layout>
  )
}

export default JwtDecoder
